/**
 * The reminders centre (§6.7, P2-TR3).
 *
 * Overdue first, then due soonest. A partner opens this to find out what has
 * been missed, not to browse — and anything already ticked off is gone from
 * the list, because a list that keeps showing done work is a list people stop
 * reading.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorMessageKey } from '../../lib/apiError.js';
import { formatDate } from '../../lib/date.js';
import { useCompleteReminder, useReminders, type Reminder } from './api.js';

const TYPE_STYLE: Record<string, string> = {
  vaccine_due: 'bg-blue-100 text-blue-900',
  delivery_due: 'bg-purple-100 text-purple-900',
  stock_low: 'bg-amber-100 text-amber-900',
  medicine_expiry: 'bg-red-100 text-red-900',
};

function urgency(reminder: Reminder): string {
  if (reminder.daysUntilDue < 0) return 'border-l-4 border-red-500';
  if (reminder.daysUntilDue === 0) return 'border-l-4 border-amber-500';
  return '';
}

export function RemindersCentre(): JSX.Element {
  const { t } = useTranslation();
  const { data: reminders = [], isLoading, isError, refetch } = useReminders();
  const complete = useCompleteReminder();

  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  async function tickOff(id: number): Promise<void> {
    setErrorKey(null);
    setBusyId(id);
    try {
      await complete.mutateAsync(id);
    } catch (error) {
      // 404 means somebody else ticked it off while this screen was open,
      // which on a four-partner farm is ordinary rather than an error.
      setErrorKey(errorMessageKey(error));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">
        {t('reminders.title')}
        {reminders.length > 0 && (
          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-sm text-amber-900">{reminders.length}</span>
        )}
      </h1>

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {errorKey !== null && (
        <p role="alert" className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          {t(errorKey)}
        </p>
      )}

      {reminders.length === 0 && !isLoading && <p className="py-8 text-center text-gray-500">{t('reminders.empty')}</p>}

      <ul className="space-y-2">
        {reminders.map((reminder) => (
          <li key={reminder.id} className={`rounded-xl bg-white p-3 shadow-sm ${urgency(reminder)}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${TYPE_STYLE[reminder.type] ?? 'bg-gray-100'}`}>
                  {t(`reminders.type.${reminder.type}`)}
                </span>
                <p className="mt-1 text-sm text-gray-800">{reminder.message}</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  {reminder.daysUntilDue < 0
                    ? t('reminders.overdue', { count: Math.abs(reminder.daysUntilDue) })
                    : reminder.daysUntilDue === 0
                      ? t('reminders.today')
                      : formatDate(reminder.dueDate)}
                </p>
              </div>

              <button
                type="button"
                onClick={() => void tickOff(reminder.id)}
                disabled={busyId === reminder.id}
                aria-label={t('reminders.done')}
                className="min-h-touch min-w-touch shrink-0 rounded-lg bg-green-100 text-green-800 disabled:opacity-50"
              >
                ✓
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
